/*
 * HomeFixer360 - Lead Capture Modal with Presale Offer
 * Copyright (c) 2025 Guy T. Dunn Sr. / Idea Guy Brands Incorporated
 * All Rights Reserved.
 */

import React, { useState } from 'react';
import { X, CheckCircle, Gift, Star, Clock, Shield, DollarSign } from 'lucide-react';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';
import SMSConsentCheckbox from './SMSConsentCheckbox';

const LeadCaptureModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    interests: [],
    smsConsent: false
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (!isOpen) return null;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          source: 'presale_popup',
          interests: formData.interests,
          notes: 'Presale lead capture from early access popup'
        })
      });

      if (response.ok) {
        setSubmitted(true);
        setTimeout(() => {
          onClose();
        }, 5000);
      } else {
        throw new Error('Failed to submit lead');
      }
    } catch (error) {
      console.error('Error submitting lead:', error);
      alert('Error submitting form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = formData.firstName && formData.lastName && formData.email && formData.phone;

  if (submitted) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8 text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">🎉 BETA TESTER SPOT SECURED! 🎉</h2>
          <p className="text-gray-600 mb-6">
            <strong>{formData.firstName}</strong>, you're one of only 50 beta testers! 
            You've locked in massive savings of up to <strong>$925</strong> plus beta access through February 28, 2026.
          </p>
          
          <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-4 mb-6 border border-green-200">
            <h3 className="font-bold text-green-800 mb-2">🎁 Your Presale Benefits:</h3>
            <div className="text-sm text-green-700 space-y-1">
              <div>✅ 50% OFF Setup Fee (Save $250)</div>
              <div>✅ 3 Months of Discounted Rates</div>
              <div>✅ Beta Access Until Feb. 28, 2026</div>
              <div>✅ 60-Day Money Back Guarantee</div>
              <div>✅ One of Only 50 Beta Testers</div>
            </div>
          </div>
          
          <p className="text-sm text-gray-500">
            This window will close automatically in a few seconds...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl max-h-[90vh] overflow-y-auto w-full">
        
        {/* Risk-Free Banner */}
        <div className="bg-green-600 text-white p-3 text-center font-bold">
          🛡️ 100% RISK-FREE PRESALE • 60-DAY MONEY BACK GUARANTEE 🛡️
        </div>

        {/* Header with Presale Offer */}
        <div className="sticky top-0 bg-gradient-to-r from-red-600 via-orange-500 to-yellow-500 text-white p-6">
          <div className="flex justify-between items-center">
            <div>
              <div className="bg-yellow-400 text-red-800 font-black text-xs px-2 py-1 rounded-full mb-2 inline-block animate-pulse">
                🔥 ONLY 50 BETA TESTERS - PRESALE ENDS JAN 31!
              </div>
              <h1 className="text-2xl font-black mb-2">MASSIVE PRESALE SAVINGS!</h1>
              <p className="text-yellow-100 font-semibold">Save $550 - $925 + Beta Access Through Jan. 15</p>
              <div className="bg-green-500 text-white text-xs px-3 py-1 rounded-full mt-2 inline-block font-bold">
                ✅ COMPLETELY RISK-FREE WITH 60-DAY GUARANTEE
              </div>
              <div className="bg-blue-600 text-white text-xs px-3 py-1 rounded-full mt-1 inline-block">
                📅 Beta: Now – Feb. 28 • Launch: Mar 1, 2026 • Presale Ends: Feb 28
              </div>
            </div>
            <button 
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors p-1 rounded-full hover:bg-white/20"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Presale Benefits */}
        <div className="p-6 bg-gradient-to-br from-yellow-50 via-orange-50 to-red-50 border-b border-orange-200">
          <div className="text-center mb-4">
            <div className="bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-xl p-4 mb-4">
              <div className="font-black text-lg">💰 TOTAL PRESALE SAVINGS 💰</div>
              <div className="grid grid-cols-3 gap-4 mt-3">
                <div className="bg-white/20 rounded-lg p-2">
                  <div className="font-bold text-sm">Starter</div>
                  <div className="text-xl font-black">$550</div>
                </div>
                <div className="bg-white/20 rounded-lg p-2">
                  <div className="font-bold text-sm">Pro</div>
                  <div className="text-xl font-black">$750</div>
                </div>
                <div className="bg-white/20 rounded-lg p-2">
                  <div className="font-bold text-sm">Enterprise</div>
                  <div className="text-xl font-black">$925</div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div className="bg-white rounded-lg p-3 border border-orange-200">
              <h3 className="font-bold text-red-600 mb-2">🎁 PRESALE INCLUDES:</h3>
              <ul className="space-y-1 text-gray-700">
                <li>✅ 50% OFF Setup Fee (Save $250)</li>
                <li>✅ Discounted Rates for 3 Months</li>
                <li>✅ Beta Access Until Jan. 15, 2026</li>
                <li>✅ One of Only 50 Beta Testers</li>
                <li className="font-bold text-green-600 bg-green-50 px-2 py-1 rounded">🛡️ 60-Day Money Back Guarantee - ZERO RISK!</li>
              </ul>
            </div>
            <div className="bg-white rounded-lg p-3 border border-orange-200">
              <h3 className="font-bold text-red-600 mb-2">⚡ MONTHLY DISCOUNTS:</h3>
              <ul className="space-y-1 text-gray-700">
                <li>• Starter: $199/month (was $299)</li>
                <li>• Pro: $332/month (was $499)</li>
                <li>• Enterprise: $774/month (was $999)</li>
                <li><strong>• Valid for First 3 Months</strong></li>
              </ul>
            </div>
          </div>

          <div className="text-center mt-4 space-y-3">
            <div className="bg-green-100 border-2 border-green-300 rounded-lg p-3">
              <div className="font-black text-green-800">🛡️ 100% RISK-FREE INVESTMENT</div>
              <div className="text-green-700 font-semibold text-sm">Don't like it? Get your full refund within 60 days - no questions asked!</div>
            </div>
            
            <div className="bg-red-100 border-2 border-red-300 rounded-lg p-3">
              <div className="font-black text-red-800">⏰ ONLY 50 BETA TESTERS - PRESALE ENDS FEBRUARY 28, 2026</div>
              <div className="text-red-600 font-semibold text-sm">Beta: Now – Feb. 28 • Launch: Mar 1, 2026 • Presale Ends: Feb 28</div>
              <div className="text-red-600 font-semibold text-sm">Secure your spot now with zero risk and huge savings!</div>
            </div>
          </div>
        </div>

        {/* Lead Capture Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Secure Your Presale Spot</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName">First Name *</Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  placeholder="John"
                  required
                />
              </div>
              <div>
                <Label htmlFor="lastName">Last Name *</Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  placeholder="Smith"
                  required
                />
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div>
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="john@company.com"
                required
              />
            </div>
            <div>
              <Label htmlFor="phone">Phone Number *</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder="+1 (555) 123-4567"
                required
              />
            </div>
          </div>

          <div className="mb-6">
            <Label htmlFor="company">Company Name</Label>
            <Input
              id="company"
              value={formData.company}
              onChange={(e) => handleInputChange('company', e.target.value)}
              placeholder="Your Home Service Company"
            />
          </div>

          {/* SMS Consent */}
          <div className="mb-6">
            <SMSConsentCheckbox
              checked={formData.smsConsent}
              onChange={(checked) => handleInputChange('smsConsent', checked)}
              companyName="HomeFixer360"
              required={false}
              variant="compact"
            />
          </div>

          <div className="border-t pt-6">
            <div className="space-y-3">
              <Button 
                type="submit"
                disabled={!isFormValid || isSubmitting}
                className="w-full bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 text-white py-4 text-lg font-bold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50"
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Securing Your Spot...</span>
                  </div>
                ) : (
                  <>🚀 SECURE RISK-FREE PRESALE SPOT</>
                )}
              </Button>
              
              <Button 
                type="button"
                onClick={() => window.location.href = '/presale-checkout'}
                className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white py-3 text-base font-bold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
              >
                💳 RISK-FREE PRESALE CHECKOUT
              </Button>
              
              <div className="text-center bg-green-50 border border-green-200 rounded-lg p-3 mt-3">
                <div className="text-green-800 font-bold text-sm">🛡️ PROTECTED BY 60-DAY MONEY BACK GUARANTEE</div>
                <div className="text-green-700 text-xs mt-1">If you're not 100% satisfied, we'll refund every penny within 60 days</div>
              </div>
            </div>
            
            <p className="text-xs text-gray-600 text-center mt-4">
              We respect your privacy. Unsubscribe at any time. No spam, just launch updates and exclusive offers.
            </p>
            
            <div className="text-center mt-3 p-2 bg-blue-50 rounded-lg">
              <div className="text-blue-800 font-semibold text-xs">💡 WHY RISK-FREE?</div>
              <div className="text-blue-700 text-xs">We're so confident you'll love HomeFixer360 that we offer a full 60-day money back guarantee. Try it completely risk-free!</div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LeadCaptureModal;